import type { GetExperiencesData } from '@/shared/api/experience';
import { unnull } from '@/shared/lib/converters';
import { EXP_TYPES } from '../../../const';
import { DEFAULT_SORTING } from '../../const/config';
import type { TExcursionFiltersPageState, TExcursionFiltersState } from '../../types/excursion/state';
import { useGrowthbook } from '@experience/core-hostapp-share-lib';

function getDefaultType() {
  const gb = useGrowthbook();
  const isTurnAudioguide = gb?.isOn('turn_on_listing_audioguide');

  if (isTurnAudioguide) {
    return `${EXP_TYPES.PRIVATE},${EXP_TYPES.GROUP},${EXP_TYPES.TICKET},${EXP_TYPES.TRANSFER_SERVICE},${EXP_TYPES.INDEPENDENT_WALK}`;
  }

  return `${EXP_TYPES.PRIVATE},${EXP_TYPES.GROUP},${EXP_TYPES.TICKET},${EXP_TYPES.TRANSFER_SERVICE}`;
}
// Для разделения фильтров формата проведения туров было
// внедрено дублирование функционала. В сторе заведено еще одно состояние tourFormat
// которое дублирует состояние type у фильтров формата экскурсий
// в апи и в урлах значение из tourFormat приравнивается к апи полю type
function buildType(filters: TExcursionFiltersState): string {
  const { type, tourFormat } = filters;

  // 1️⃣ Экскурсии: приоритет у type
  if (type && type.length) {
    return type.join(',');
  }

  // 2️⃣ Если экскурсий нет, но есть туровые форматы — используем их
  if (tourFormat && tourFormat.length) {
    const set = new Set<string>([...tourFormat]);

    return Array.from(set).join(',');
  }

  return getDefaultType();
}

function addTourToType(type: string) {
  const parts = type
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.includes('tour')) {
    parts.push('tour');
  }

  return parts.join(',');
}

export function buildExcursionAggregationTypeWithoutTours(filters: TExcursionFiltersState): string {
  if (filters.type && filters.type.length) {
    const typeWithoutTours = filters.type
      .join(',')
      .split(',')
      .map((type) => type.trim())
      .filter((type) => type && type !== 'tour' && !type.startsWith('tour_'))
      .join(',');

    return typeWithoutTours || getDefaultType();
  }

  return getDefaultType();
}

function shouldAddTourToType(
  filters: TExcursionFiltersState,
  pageParams: TExcursionFiltersPageState,
  options?: { skipAddTourToType?: boolean; forceAddTourToType?: boolean },
): boolean {
  if (options?.skipAddTourToType) {
    return false;
  }

  const isCityPage = !!pageParams.city;
  const isRubricSelected = !!filters.tag__slugs && filters.tag__slugs !== 'all';
  const userSelectedFormat = !!filters.type.length || !!filters.tourFormat.length;

  // Если в городе и выбран формат (для любой рубрики) - НЕ добавляем tour
  if (isCityPage && userSelectedFormat) {
    return false;
  }

  if (options?.forceAddTourToType) {
    return true;
  }

  // Если в городе, выбрана рубрика с включенными турами, не туры листинг и не выбран формат - добавляем tour
  const isRubricWithTours = filters.selected_rubric?.is_tours_included === true;

  return isCityPage && isRubricSelected && isRubricWithTours && !userSelectedFormat;
}

export function excursionFiltersToQuery(
  filters: TExcursionFiltersState,
  pageParams: TExcursionFiltersPageState,
  options?: { skipAddTourToType?: boolean; forceAddTourToType?: boolean },
) {
  let type = buildType(filters);

  const [startPrice, endPrice] = filters.price;
  const [startDuration, endDuration] = filters.duration;

  const movementTypes = filters.movementTypes ? filters.movementTypes.join(',') : filters.movementTypes;

  const sorting = filters.sorting !== DEFAULT_SORTING ? filters.sorting : null;

  const childrenAllowed = filters.childrenAllowed || undefined;

  const startTimeInterval = filters.startTimeInterval.length ? filters.startTimeInterval.join(',') : undefined;
  const durationInterval = filters.durationInterval.length ? filters.durationInterval.join(',') : undefined;
  // Баг на беке, TODO: выпилить каленым железом когда бек починят https://tripster.atlassian.net/browse/EXP-10237
  const normalizeDuration = (val: number | null) => {
    if (val === null) {
      return null;
    }

    // вычитаем 24 часа, только если значение ≥ 24
    return val >= 24 ? val - 24 : val;
  };

  // ✅ Важно: добавляем 'tour' только когда тип НЕ выбран пользователем (иначе ломаем "формат проведения")
  if (shouldAddTourToType(filters, pageParams, options) && !type.split(',').includes('tour')) {
    type = addTourToType(type);
  }

  const requestQuery: GetExperiencesData['query'] = {
    city__slug: pageParams.city,
    end_date: unnull(filters.date.to),
    end_price: unnull(endPrice),
    movement_type: unnull(movementTypes),
    persons_count: unnull(filters.date.persons),
    start_date: unnull(filters.date.from),
    start_duration: unnull(normalizeDuration(startDuration)),
    end_duration: unnull(normalizeDuration(endDuration)),
    start_price: unnull(startPrice),
    tag__slugs: unnull(filters.tag__slugs),
    type: unnull(type),
    children_allowed: childrenAllowed,
    start_time_interval: startTimeInterval,
    duration_interval: durationInterval,

    // @ts-expect-error TODO: чекнуть что там с сортировкой
    sorting,
  };

  return requestQuery;
}
